/* Zero-dependency spotlight tour. A full-screen blocker captures clicks, a
   box-shadow "spotlight" dims everything except the target rect, and a card is
   anchored beside it. No tour library. */
/* Above Mantine's `max` z-index (9999): some menus (e.g. the navbar Browse
   dropdown) portal at that level, and the spotlight must paint over them. */
.root {
    position: fixed;
    inset: 0;
    z-index: var(--ld-z-tour);
}

/* interactive steps let clicks through to the page; only the card catches
   pointer events (it sets pointer-events: auto itself) */
.rootInteractive {
    pointer-events: none;
}

/* transparent layer that blocks interaction with the page underneath. On an
   interactive step four of these frame the spotlight, leaving only the
   highlighted control clickable. */
.blocker {
    position: fixed;
    top: var(--blk-top, 0);
    left: var(--blk-left, 0);
    width: var(--blk-width, 100vw);
    height: var(--blk-height, 100vh);
    pointer-events: auto;
}

/* full dim used when a step has no target (centered explainer) */
.dim {
    position: absolute;
    inset: 0;
    background: light-dark(rgba(17, 24, 39, 0.62), rgba(0, 0, 0, 0.75));
}

/* the cutout: its huge box-shadow darkens the rest of the screen, and a ring
   marks the showcased feature so it reads as highlighted, not just un-dimmed */
.spotlight {
    position: fixed;
    top: var(--tour-top);
    left: var(--tour-left);
    width: var(--tour-width);
    height: var(--tour-height);
    border-radius: var(--mantine-radius-md);
    box-shadow:
        0 0 0 2px var(--mantine-color-indigo-4),
        0 0 0 6px light-dark(rgba(76, 87, 217, 0.25), rgba(129, 140, 248, 0.22)),
        0 0 0 9999px light-dark(rgba(17, 24, 39, 0.62), rgba(0, 0, 0, 0.75));
    pointer-events: none;
}

/* the ring shrunk to a point after its control was clicked: a small glowing
   light that waits where the click was until the next control is on screen */
.spotlightBeacon {
    border-radius: 50%;
}

.spotlightBeacon::before {
    content: '';
    position: absolute;
    inset: -9px;
    border-radius: 50%;
    background: radial-gradient(
        circle,
        var(--mantine-color-indigo-3) 0%,
        light-dark(rgba(129, 140, 248, 0.55), rgba(129, 140, 248, 0.6)) 45%,
        transparent 72%
    );
    animation: ldTourBeacon 1.4s ease-in-out infinite;
}

/* While the next control is still loading, an arc circles the beacon so the
   wait reads as loading rather than as a stall. */
.spotlightWaiting::after {
    content: '';
    position: absolute;
    inset: -16px;
    border-radius: 50%;
    border: 2px solid transparent;
    border-top-color: var(--mantine-color-indigo-4);
    border-right-color: var(--mantine-color-indigo-4);
    animation: ldTourWaiting 0.9s linear infinite;
}

/* While the beacon stands in for the cursor, the native pointer is hidden
   everywhere (the class sits on the document, see GuidedTour.tsx). */
:global(.ld-tour-beacon-cursor),
:global(.ld-tour-beacon-cursor) * {
    cursor: none !important;
}

@keyframes ldTourWaiting {
    to {
        transform: rotate(360deg);
    }
}

@keyframes ldTourBeacon {
    0%,
    100% {
        transform: scale(1);
        opacity: 0.85;
    }
    50% {
        transform: scale(1.35);
        opacity: 1;
    }
}

/* only while travelling to a new control; otherwise the ring follows scroll
   and resize instantly */
.spotlightGliding {
    transition:
        top 0.6s cubic-bezier(0.4, 0, 0.2, 1),
        left 0.6s cubic-bezier(0.4, 0, 0.2, 1),
        width 0.6s cubic-bezier(0.4, 0, 0.2, 1),
        height 0.6s cubic-bezier(0.4, 0, 0.2, 1),
        border-radius 0.3s ease;
}

.card {
    position: fixed;
    top: var(--tour-card-top);
    left: var(--tour-card-left);
    width: var(--tour-card-width);
    max-width: calc(100vw - 24px);
    pointer-events: auto;
    z-index: var(--ld-z-tour-card);
}

/* the card follows the ring into the beacon and back out: it shrinks towards
   its caret (the ring) and grows from there at the next control */
.cardCollapse {
    transform-origin: var(--tour-caret-left, 50%) var(--tour-origin-y, 0);
    animation: ldTourCardCollapse 250ms ease-in forwards;
}

.cardExpand {
    transform-origin: var(--tour-caret-left, 50%) var(--tour-origin-y, 0);
    animation: ldTourCardExpand 280ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

@keyframes ldTourCardCollapse {
    to {
        transform: scale(0.1);
        opacity: 0;
    }
}

@keyframes ldTourCardExpand {
    from {
        transform: scale(0.1);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

.cardGliding {
    transition:
        top 0.6s cubic-bezier(0.4, 0, 0.2, 1),
        left 0.6s cubic-bezier(0.4, 0, 0.2, 1),
        width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* The control whose click moves the tour forward: a soft pulse radiating
   from the ring so the eye lands on it. */
.spotlightPulse::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: inherit;
    border: 2px solid var(--mantine-color-indigo-4);
    animation: ldTourPulse 1.6s ease-out infinite;
    pointer-events: none;
}

/* Same pulse on the card button when pressing it is what moves the tour on.
   Mantine's button clips its overflow, which would hide the ring. */
.buttonPulse {
    position: relative;
    overflow: visible;
}

.buttonPulse::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: calc(var(--mantine-radius-default) + 3px);
    border: 2px solid var(--mantine-color-indigo-4);
    animation: ldTourPulse 1.6s ease-out infinite;
    pointer-events: none;
}

@keyframes ldTourPulse {
    0% {
        opacity: 0.9;
        transform: scale(1);
    }
    70% {
        opacity: 0;
        transform: scale(1.12);
    }
    100% {
        opacity: 0;
        transform: scale(1.12);
    }
}

/* while the page works on a step, the card parks in the top-right corner
   instead of following a ring that is still moving and reshaping */
.cardDocked {
    position: fixed;
    top: 72px;
    right: 24px;
    width: 360px;
    max-width: calc(100vw - 24px);
    pointer-events: auto;
    z-index: var(--ld-z-tour-card);
    animation: ldTourCardDock 300ms ease-out;
}

@keyframes ldTourCardDock {
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.cardCentered {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 460px;
    max-width: calc(100vw - 24px);
    pointer-events: auto;
    z-index: var(--ld-z-tour-card);
}

/* The card must not read as regular UI: deep elevation and a coachmark caret
   pointing back at the spotlight. */
.paper {
    position: relative;
    background: var(--mantine-color-body);
    box-shadow: 0 18px 45px
        light-dark(rgba(17, 24, 39, 0.22), rgba(0, 0, 0, 0.6));
    animation: ldTourCardIn 160ms ease-out;
}

@keyframes ldTourCardIn {
    from {
        opacity: 0;
        transform: translateY(-4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.caret {
    position: absolute;
    left: var(--tour-caret-left);
    width: 0;
    height: 0;
    margin-left: -8px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
}

.caretTop {
    top: -8px;
    border-bottom: 8px solid var(--mantine-color-body);
}

.caretBottom {
    bottom: -8px;
    border-top: 8px solid var(--mantine-color-body);
}

.eyebrow {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: light-dark(
        var(--mantine-color-indigo-6),
        var(--mantine-color-indigo-3)
    );
}

.dots {
    display: flex;
    gap: 6px;
    align-items: center;
}

.dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--mantine-color-ldGray-3);
}

/* light-dark() on both rules keeps them at the same specificity, so this one
   still wins over .dot in either scheme. */
.dotActive {
    background: light-dark(
        var(--mantine-color-indigo-5),
        var(--mantine-color-indigo-4)
    );
    transform: scale(1.15);
}

/* The ring and card still move between controls, just without the glide,
   and the beacon holds still rather than pulsing. */
@media (prefers-reduced-motion: reduce) {
    .spotlightGliding,
    .cardGliding {
        transition: none;
    }
    .spotlightBeacon::before {
        animation: none;
    }
}

/* A suggestion of more than one line (a block of YAML the learner would not
   retype) printed as code: it scrolls sideways rather than widening the card. */
.suggestionBlock {
    max-width: 100%;
    overflow-x: auto;
    white-space: pre;
    margin: 0;
}

/* Already in the file: where the highlighted lines go, not something to type. */
.suggestionContext {
    opacity: 0.45;
}

.suggestionInput {
    color: light-dark(
        var(--mantine-color-ldBrandViolet-7),
        var(--mantine-color-ldBrandViolet-3)
    );
    font-weight: 600;
}
